import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { SafeUrl } from '@angular/platform-browser';
import { marker } from '@colsen1991/ngx-translate-extract-marker';
import { IconProp } from '@fortawesome/fontawesome-svg-core';


export interface SelectedImage {
  file: File;
  imageUrl: SafeUrl;
}

@Component({
  selector: 'rk-image-upload',
  templateUrl: './image-upload.component.html',
  styleUrls: ['./image-upload.component.scss'],
  standalone: false,
})
export class ImageUploadComponent implements OnInit, OnChanges, OnDestroy {
  @Input() fetchedImageUrl: string | SafeUrl;
  @Input() title: string = null;
  @Input() description: string;
  @Input() technicalRequirementsText?: string;
  @Input() deleteButton = true;
  @Input() hideCancelButton?: boolean;
  @Input() mockImageUrl?: string;
  @Input() iconSize? = 50;
  @Input() icon?: IconProp;
  // progress config (for the circle-progress component)
  @Input() radius = 50;
  @Input() strokeWidth = 10;
  @Input() mockUploadTime = 1500; // ms — duration of the fake upload animation
  @Input() compactLayout?: boolean;
  @Input() aspectRatio?: string;
  @Input() containerAspectRatio: string;
  @Input() width = '100%';
  @Input() height?: string;
  @Input() horizontal?: boolean;

  @Output() readonly fileSelected = new EventEmitter<SelectedImage>();
  @Output() readonly uploadCanceled = new EventEmitter();
  @Output() readonly imageRemoved = new EventEmitter();

  file: File;
  uploadProgress = 0;
  imageUrl: SafeUrl;
  accepts = '.png,.jpg,.jpeg';
  draggingOver = false;
  private mockUploadTask: ReturnType<typeof setInterval>;
  MAX_FILE_SIZE = 10485760; // 10 MB
  errorMessage: string = null;

  ngOnInit(): void {
    if (this.fetchedImageUrl) {
      this.imageUrl = this.fetchedImageUrl;
    }
  }

  // skip external url updates while a mock upload is in progress
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['fetchedImageUrl'] && this.uploadProgress === 0) {
      this.imageUrl = this.fetchedImageUrl;
    }
  }

  @HostListener('dragover', ['$event']) onDragOver(evt: DragEvent) {
    evt.preventDefault();
    evt.stopPropagation();
    this.draggingOver = true;
  }

  @HostListener('dragleave', ['$event']) onDragLeave(evt: DragEvent) {
    evt.preventDefault();
    evt.stopPropagation();
    this.draggingOver = false;
  }

  @HostListener('drop', ['$event']) onDrop(evt: DragEvent) {
    evt.preventDefault();
    evt.stopPropagation();
    this.draggingOver = false;
    const files = evt.dataTransfer?.files;
    if (files && files.length > 0) {
      const file = files[0];
      this.onFileSelected(file);
    }
  }

  @ViewChild('fileInput')
  fileInput!: ElementRef;

  openFileInput(event: MouseEvent): void {
    event.stopPropagation();
    this.fileInput.nativeElement.click();
  }

  onFileSelected(file: File) {
    this.draggingOver = false;
    this.resetFile();

    if (!this.isValidFileType(file)) {
      this.displayErrorMessage(marker('image-upload.file-type-error'));

      return;
    }

    if (file.size > this.MAX_FILE_SIZE) {
      this.displayErrorMessage(marker('image-upload.file-size-error'));

      return;
    }

    this.file = file;
    // fake upload animation — ticks every 10ms until mockUploadTime is reached,
    // then reads the file and emits fileSelected
    const updateInterval = 10;
    let currentProgress = 0;
    this.mockUploadTask = setInterval(() => {
      const progressIncrement = (updateInterval / this.mockUploadTime) * 100;
      currentProgress += progressIncrement;
      const roundedProgress = Math.round(currentProgress);

      if (roundedProgress >= 100) {
        currentProgress = 100;
        this.updateProgress(100);
        clearInterval(this.mockUploadTask);
        // read file only after progress completes
        const reader = new FileReader();
        reader.onload = () => {
          this.imageUrl = reader.result;
          this.fileSelected.emit({ file, imageUrl: this.imageUrl });
        };
        reader.readAsDataURL(file);
      } else {
        this.updateProgress(roundedProgress);
      }
    }, updateInterval);
  }

  onFileChange(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length) {
      const file = input.files[0];
      this.onFileSelected(file);
      this.fileInput.nativeElement.value = '';
    }
  }

  updateProgress(progress: number) {
    this.uploadProgress = progress;
  }

  cancelUpload() {
    this.resetFile();
    clearInterval(this.mockUploadTask);
    this.uploadCanceled.emit();
  }

  removeImage() {
    this.resetFile();
    this.imageRemoved.emit();
  }

  public resetFile() {
    this.uploadProgress = 0;
    this.file = null;
    this.imageUrl = null;
  }

  private isValidFileType(file: File): boolean {
    const acceptedExtensions = this.accepts.split(',').map(ext => ext.trim().toLowerCase());

    return acceptedExtensions.some(ext =>
      file.name.toLowerCase().endsWith(ext)
    );
  }

  private displayErrorMessage(message: string) {
    this.errorMessage = message;
    setTimeout(() => {
      this.errorMessage = null;
    }, 5000);
  }

  ngOnDestroy(): void {
    if (this.mockUploadTask) {
      clearInterval(this.mockUploadTask);
    }
  }
}
